import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  TextField,
  MenuItem,
  InputLabel,
  Select,
  SelectChangeEvent,
  Divider,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  CalendarMonth as CalendarIcon,
  Build as BuildIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { PoolSettings, PoolSettingsUpdate } from '../../types';

interface ScheduleSettingsFormProps {
  settings: PoolSettings['schedule'];
  onChange: (data: PoolSettingsUpdate['schedule']) => void;
}

const ScheduleSettingsForm: React.FC<ScheduleSettingsFormProps> = ({
  settings,
  onChange,
}) => {
  const [formData, setFormData] = useState({
    opening_time: settings.opening_time,
    closing_time: settings.closing_time,
    maintenance_day: settings.maintenance_day,
    maintenance_time: settings.maintenance_time,
  });

  // settings prop이 변경되면 formData 업데이트
  useEffect(() => {
    setFormData({
      opening_time: settings.opening_time,
      closing_time: settings.closing_time,
      maintenance_day: settings.maintenance_day,
      maintenance_time: settings.maintenance_time,
    });
  }, [settings]);

  // 텍스트 필드 값 변경 핸들러
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);
    onChange(newFormData);
  };

  // 셀렉트 필드 값 변경 핸들러
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);
    onChange(newFormData);
  };

  // 요일 한글 변환 함수
  const getDayLabel = (day: string) => {
    const dayMap: Record<string, string> = {
      monday: '월요일',
      tuesday: '화요일',
      wednesday: '수요일',
      thursday: '목요일',
      friday: '금요일',
      saturday: '토요일',
      sunday: '일요일',
      none: '없음',
    };
    return dayMap[day] || day;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        운영 일정 설정
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        시설의 운영 시간과 정기 유지보수 일정을 설정. 이 정보는 시설 관리와 예약 시스템에 사용됨.
      </Typography>

      <Grid container spacing={3}>
        {/* 운영 시간 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="운영 시간" 
              subheader="시설 개장/폐장 시간"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
              avatar={<ScheduleIcon color="primary" />}
              action={
                <Tooltip title="시설 이용객에게 공지되는 일일 운영 시간">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="개장 시간"
                    type="time"
                    name="opening_time"
                    value={formData.opening_time}
                    onChange={handleTimeChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <TimeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      ),
                    }}
                    inputProps={{
                      step: 300, // 5분 단위
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="폐장 시간"
                    type="time"
                    name="closing_time"
                    value={formData.closing_time}
                    onChange={handleTimeChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <TimeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      ),
                    }}
                    inputProps={{
                      step: 300, // 5분 단위
                    }}
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {formData.opening_time !== '' && formData.closing_time !== '' && (
                  <>
                    운영 시간: 매일 {formData.opening_time}부터 {formData.closing_time}까지
                    <br />
                    (총 운영 시간: {calcOperatingHours(formData.opening_time, formData.closing_time)})
                  </>
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* 유지보수 일정 설정 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="정기 유지보수 일정" 
              subheader="정기적인 시설 관리/청소 일정"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
              avatar={<BuildIcon color="primary" />}
              action={
                <Tooltip title="정기적인 수질 관리 및 시설 점검을 위한 휴장일과 시간을 설정.">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="maintenance-day-label">유지보수 요일</InputLabel>
                    <Select
                      labelId="maintenance-day-label"
                      name="maintenance_day"
                      value={formData.maintenance_day}
                      onChange={handleSelectChange}
                      label="유지보수 요일"
                      startAdornment={
                        <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      }
                    >
                      <MenuItem value="none">없음</MenuItem>
                      <MenuItem value="monday">월요일</MenuItem>
                      <MenuItem value="tuesday">화요일</MenuItem>
                      <MenuItem value="wednesday">수요일</MenuItem>
                      <MenuItem value="thursday">목요일</MenuItem>
                      <MenuItem value="friday">금요일</MenuItem>
                      <MenuItem value="saturday">토요일</MenuItem>
                      <MenuItem value="sunday">일요일</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="유지보수 시간"
                    type="time"
                    name="maintenance_time"
                    value={formData.maintenance_time}
                    onChange={handleTimeChange}
                    fullWidth
                    disabled={formData.maintenance_day === 'none'}
                    InputProps={{
                      startAdornment: (
                        <TimeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      ),
                    }}
                    inputProps={{
                      step: 300, // 5분 단위
                    }}
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {formData.maintenance_day !== 'none' ? (
                  <>
                    정기 유지보수: 매주 {getDayLabel(formData.maintenance_day)} {formData.maintenance_time || '종일'}
                    <br />
                    유지보수 일정에는 시설이 일부 또는 전체 이용이 제한되기 가능.
                  </>
                ) : (
                  '정기 유지보수 일정이 설정되지 않음'
                )}
              </Typography>
              
              {formData.maintenance_day !== 'none' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  정기 유지보수 시간은 자동으로 예약 시스템에 반영되며 이용 불가 시간으로 표시됨.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 운영 시간 안내 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Alert severity="success" icon={<ScheduleIcon />}>
                <Typography variant="subtitle2">운영 시간 요약</Typography>
                <Typography variant="body2">
                  운영 시간: 매일 {formData.opening_time || '--:--'}부터 {formData.closing_time || '--:--'}까지
                  {formData.maintenance_day !== 'none' && (
                    <>
                      <br />
                      정기 휴무: 매주 {getDayLabel(formData.maintenance_day)} {formData.maintenance_time ? `(${formData.maintenance_time} 부터)` : ''}
                    </>
                  )}
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// 운영 시간 계산 함수
function calcOperatingHours(openTime: string, closeTime: string): string {
  if (!openTime || !closeTime) return '';

  const [openHour, openMin] = openTime.split(':').map(Number);
  const [closeHour, closeMin] = closeTime.split(':').map(Number);

  let totalMinutes = (closeHour * 60 + closeMin) - (openHour * 60 + openMin);
  
  // 폐장 시간이 개장 시간보다 이전인 경우 (다음날 새벽까지 운영)
  if (totalMinutes < 0) {
    totalMinutes += 24 * 60;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}시간 ${minutes > 0 ? `${minutes}분` : ''}`;
}

export default ScheduleSettingsForm; 